import { useEffect, useState } from "react";

import {
  BOOKING_FLOW_EVENTS,
  INSURANCE_ELIGIBILITY,
  useBookingFlowEvents,
  useStatesListQuery,
  useSyncEmailEnteredToPauboxMutation,
  VISIT_TYPES,
  type VisitType,
} from "~/services";
import { INSURANCE_NOT_FOUND_OPTION } from "~/shared.types";
import { InsuranceDropdown, StateDropdown } from "~/shared/components";
import { useBookingFlowStore } from "~/stores";
import { icons } from "~/ui";
import { FieldHint } from "../components/field-hint";
import { RegistrationAlertDialog } from "../components/registration-alert-dialog";
import { StaffBookingFooter } from "../components/staff-booking-footer";
import { StepHeading } from "../components/step-heading";
import type { StaffBookingData } from "../staff-booking-types";
import {
  getRegistrationStatus,
  REGISTRATION_STATUS,
  type RegistrationStatus,
} from "../utils/get-registration-status";
import type { StaffBookingRegistration } from "../utils/staff-booking-patient-schema";

interface StateInsuranceStepProps {
  email: string;
  visitType: VisitType;
  stateId: StaffBookingData["stateId"];
  insuranceId: StaffBookingData["insuranceId"];
  registrations: StaffBookingRegistration[];
  onChange: (patch: Pick<StaffBookingData, "stateId" | "insuranceId">) => void;
  onContinue: () => void;
  onBack: () => void;
}

export const StateInsuranceStep = ({
  email,
  visitType,
  stateId,
  insuranceId,
  registrations,
  onChange,
  onContinue,
  onBack,
}: StateInsuranceStepProps) => {
  const { trackTimeEvent, trackEvent } = useBookingFlowEvents();
  const bookingFlow = useBookingFlowStore((state) => state.flow);
  const { mutate: syncEmailEnteredToPaubox } =
    useSyncEmailEnteredToPauboxMutation();

  const [isEditingState, setIsEditingState] = useState(stateId === null);
  const [registrationStatus, setRegistrationStatus] =
    useState<RegistrationStatus | null>(null);

  useEffect(() => {
    if (bookingFlow?.id) {
      trackTimeEvent(
        BOOKING_FLOW_EVENTS.INSURANCE_PAGE_VIEWED_AT,
        bookingFlow.id,
      );
    }
  }, [bookingFlow?.id]);

  const isCoach = visitType === VISIT_TYPES.COACH;

  const { data: states, isLoading: statesLoading } = useStatesListQuery({
    isEligible: !isCoach,
  });

  const selectedState = states?.find((state) => state.id === stateId);
  const selectedStateEligible = selectedState?.isEligible ?? false;

  const handleStateChange = (value: number | null) => {
    onChange({ stateId: value, insuranceId: null });
    if (value !== null) {
      trackEvent(BOOKING_FLOW_EVENTS.STATE_ID, value);
    }
  };

  const handleInsuranceChange = (value: number | null) => {
    const previousValueNotListed =
      insuranceId === INSURANCE_NOT_FOUND_OPTION.value;
    onChange({ stateId, insuranceId: value });

    if (value === INSURANCE_NOT_FOUND_OPTION.value) {
      trackEvent(BOOKING_FLOW_EVENTS.IS_NOT_LISTED_INSURANCE, true);
      return;
    }

    if (previousValueNotListed) {
      trackEvent(BOOKING_FLOW_EVENTS.IS_NOT_LISTED_INSURANCE, false);
    }
    if (value !== null) {
      trackEvent(BOOKING_FLOW_EVENTS.INSURANCE_ID, value);
    }
  };

  const advance = () => {
    setRegistrationStatus(null);

    if (selectedState) {
      trackEvent(BOOKING_FLOW_EVENTS.STATE_ID, selectedState.id);
    }
    if (insuranceId === INSURANCE_NOT_FOUND_OPTION.value) {
      trackEvent(BOOKING_FLOW_EVENTS.IS_NOT_LISTED_INSURANCE, true);
    } else if (insuranceId) {
      trackEvent(BOOKING_FLOW_EVENTS.INSURANCE_ID, insuranceId);
    }
    trackTimeEvent(BOOKING_FLOW_EVENTS.INSURANCE_PAGE_SUBMITTED_AT);

    if (
      visitType === VISIT_TYPES.INITIAL &&
      email &&
      selectedState &&
      insuranceId &&
      insuranceId !== INSURANCE_NOT_FOUND_OPTION.value
    ) {
      syncEmailEnteredToPaubox({
        email,
        stateId: selectedState.id,
        insuranceId,
      });
    }

    onContinue();
  };

  const handleContinue = () => {
    if (!selectedState) return;

    const status = isCoach
      ? REGISTRATION_STATUS.REGISTERED
      : getRegistrationStatus({ registrations, state: selectedState });

    if (status === REGISTRATION_STATUS.REGISTERED) {
      advance();
      return;
    }

    setRegistrationStatus(status);
  };

  const disabled = isCoach
    ? statesLoading || !selectedState || (selectedStateEligible && !insuranceId)
    : !selectedState || !insuranceId;

  const showInsurance = !isCoach || !selectedState || selectedStateEligible;
  const isStatesPlaceholder = statesLoading && !isEditingState;
  const showStateDropdown =
    !isStatesPlaceholder && (isEditingState || !selectedState);

  const stateHint = (
    <FieldHint>
      This helps us match the patient with a clinician licensed in the selected
      state. The patient must be physically present in this state at the time of
      the visit.
    </FieldHint>
  );

  return (
    <div className="flex min-h-full flex-col gap-6">
      <StepHeading title="Book a visit" visitType={visitType} />

      <div className="flex flex-col gap-4">
        <div>
          {isStatesPlaceholder ? (
            <div className="h-11" aria-hidden="true" />
          ) : showStateDropdown ? (
            <StateDropdown
              value={stateId}
              isEligible={!isCoach}
              onChange={handleStateChange}
              description={stateHint}
            />
          ) : (
            <div className="flex flex-col gap-1">
              <p className="text-dark-blue-gray text-sm">State</p>
              <div className="flex h-11 items-center gap-4">
                <span className="text-indigo-charcoal text-lg">
                  {selectedState?.name}
                </span>
                <button
                  type="button"
                  aria-label="Edit state"
                  className="text-indigo rounded-full p-1 transition-opacity hover:opacity-70"
                  onClick={() => setIsEditingState(true)}
                >
                  <icons.outline.PencilIcon className="size-6" />
                </button>
              </div>
            </div>
          )}
          {!showStateDropdown && stateHint}
        </div>

        {showInsurance && (
          <div>
            <InsuranceDropdown
              value={insuranceId}
              onChange={handleInsuranceChange}
              stateId={selectedState?.id ?? null}
              eligibility={
                isCoach ? undefined : [INSURANCE_ELIGIBILITY.IN_NETWORK]
              }
              disabled={!selectedState}
              description={
                <FieldHint>
                  This helps us match the patient with a clinician in network
                  with the selected plan.
                </FieldHint>
              }
            />
          </div>
        )}
      </div>

      <StaffBookingFooter
        onBack={onBack}
        continueProps={{
          type: "button",
          onClick: handleContinue,
          disabled,
        }}
      />

      <RegistrationAlertDialog
        status={registrationStatus}
        state={selectedState}
        onContinue={advance}
        onCancel={() => {
          setRegistrationStatus(null);
          setIsEditingState(true);
        }}
      />
    </div>
  );
};
